import { useState } from 'react';
import { CheckSquare, ChevronDown, Loader2, X } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'in_warehouse', label: 'En Bodega' },
  { value: 'in_fastmax',   label: 'En FastMax' },
  { value: 'with_driver',  label: 'Con Conductor' },
  { value: 'delivered',    label: 'Entregado' },
  { value: 'returned',     label: 'Devuelto' },
];

/**
 * BulkActionBar
 *
 * Slides in at the top of the table when one or more rows are selected.
 * Allows changing the status of all selected packages in one action.
 */
export function BulkActionBar({ selectedIds, onBulkStatus, onClearSelection, loading }) {
  const [targetStatus, setTargetStatus] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  if (selectedIds.length === 0) return null;

  const handleConfirm = async () => {
    if (!targetStatus) return;
    await onBulkStatus(targetStatus);
    setTargetStatus('');
    setShowDropdown(false);
  };

  const selectedLabel = STATUS_OPTIONS.find(o => o.value === targetStatus)?.label;

  return (
    <div
      className="bg-brand-primary rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 shadow-lg"
      style={{ animation: 'slideDown 0.2s ease-out' }}
    >
      <style>{`@keyframes slideDown { from { transform: translateY(-10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      {/* Selection count */}
      <div className="flex items-center gap-2 shrink-0">
        <CheckSquare size={18} className="text-white" />
        <span className="font-montserrat font-bold text-white text-sm">
          {selectedIds.length} seleccionado{selectedIds.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="hidden sm:block flex-1" />

      {/* Status picker */}
      <div className="relative">
        <button
          onClick={() => setShowDropdown(prev => !prev)}
          className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white font-montserrat font-semibold text-sm px-3 py-2 rounded-lg transition-colors"
        >
          {selectedLabel || 'Cambiar estado'}
          <ChevronDown size={14} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {showDropdown && (
          <div className="absolute top-full mt-1 right-0 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50 min-w-[160px]">
            {STATUS_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => { setTargetStatus(opt.value); setShowDropdown(false); }}
                className={`w-full text-left px-6 py-2.5 text-sm font-montserrat hover:bg-gray-50 transition-colors ${
                  targetStatus === opt.value ? 'text-brand-primary font-bold' : 'text-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Confirm button */}
      <button
        onClick={handleConfirm}
        disabled={!targetStatus || loading}
        className="flex items-center gap-2 bg-white text-brand-primary font-montserrat font-bold text-sm px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shrink-0"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : null}
        Aplicar
      </button>

      {/* Clear selection */}
      <button
        onClick={onClearSelection}
        className="text-white/70 hover:text-white transition-colors shrink-0"
      >
        <X size={18} />
      </button>
    </div>
  );
}
